import React from 'react';
import { getCategoryLabel } from '../../utils/expenseCategories';
import { getInvoiceCap, isSettledOnApproval } from '../../utils/expenseWorkflow';

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

// Matches ExpenseList's formatter: a date-only string is a calendar date, so it
// must not be pulled into the local timezone by the Date constructor.
const formatDate = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const match = DATE_ONLY_PATTERN.exec(value.trim());
    if (match) {
      const local = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      return local.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

const employeeName = (expense) => {
  const first = expense?.first_name || expense?.employee_first_name || '';
  const last = expense?.last_name || expense?.employee_last_name || '';
  return `${first} ${last}`.trim() || 'Unknown Employee';
};

const initials = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || '?';

/**
 * One expense on the board.
 *
 * Apart from the QBO checkbox the card carries no event handlers of its own: it
 * sits inside a draggable wrapper that owns the pointer, and is reused unchanged
 * as the drag overlay.
 *
 * `onToggleQboReceipt` is only passed for the Approved and Paid column, which is
 * the one place the flag is worth editing — it also switches the read-only "In
 * QBO" pill for a live checkbox.
 *
 * `inGroup` is set for a card drawn inside a group block, where the block's own
 * header already names the group and the card's group tag would just repeat it.
 */
export default function ExpenseKanbanCard({ expense, isOverlay = false, inGroup = false, onToggleQboReceipt }) {
  const name = employeeName(expense);
  const attachments = Number(expense?.attachment_count || 0);
  const inQbo = Boolean(expense?.receipt_uploaded_to_qbo);
  const editableQbo = Boolean(onToggleQboReceipt) && !isOverlay;
  const billable = Boolean(expense?.customer_reimbursable);
  const customer = String(expense?.customer_name || '').trim();
  // The card goes on showing what we paid, so the cap has to say for itself
  // that this one did not come back in full.
  const invoiceCap = getInvoiceCap(expense);

  const className = [
    'expense-kanban-card',
    isOverlay ? 'expense-kanban-card--overlay' : '',
    // Money that comes back from a customer is worth spotting without reading
    // the card, so the accent stripe changes colour too.
    billable ? 'expense-kanban-card--billable' : '',
  ].filter(Boolean).join(' ');

  return (
    <article className={className}>
      <div className="expense-kanban-card__head">
        <span className="expense-kanban-card__avatar" aria-hidden="true">{initials(name)}</span>
        <span className="expense-kanban-card__employee" title={name}>{name}</span>
        <span className="expense-kanban-card__amount">{formatCurrency(expense?.amount)}</span>
      </div>

      {/* Vendor and description share a line — each is short enough on its own
          that a row apiece was mostly empty space. Either may be absent, and an
          absent one is simply left out rather than announced. */}
      {expense?.vendor_name || expense?.description ? (
        <div className="expense-kanban-card__line">
          {expense?.vendor_name ? (
            <span className="expense-kanban-card__vendor" title={expense.vendor_name}>
              <span className="expense-kanban-card__label">Vendor:</span> {expense.vendor_name}
            </span>
          ) : null}
          {expense?.vendor_name && expense?.description ? (
            <span className="expense-kanban-card__dot" aria-hidden="true">·</span>
          ) : null}
          {expense?.description ? (
            <span className="expense-kanban-card__description" title={expense.description}>
              <span className="expense-kanban-card__label">Description:</span> {expense.description}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Its own bar rather than another pill in the tag row: the customer is a
          name of arbitrary length, and the flag is the one thing on the card
          that decides whether the money is ours to eat. A reimbursable expense
          with no customer on it is a gap worth showing, not hiding. */}
      {billable ? (
        <div className="expense-kanban-card__billable">
          <i className="bi bi-arrow-repeat" aria-hidden="true" />
          <span className="expense-kanban-card__billable-label">Customer reimbursable</span>
          {customer ? (
            <span className="expense-kanban-card__billable-customer" title={customer}>{customer}</span>
          ) : (
            <span className="expense-kanban-card__billable-customer expense-kanban-card__billable-customer--missing">
              No customer set
            </span>
          )}
        </div>
      ) : null}

      <div className="expense-kanban-card__tags">
        <span className="expense-kanban-tag expense-kanban-tag--category">
          {getCategoryLabel(expense?.category)}
        </span>
        {isSettledOnApproval(expense) ? (
          <span className="expense-kanban-tag expense-kanban-tag--card">
            <i className="bi bi-credit-card-2-front" aria-hidden="true" /> Company card
          </span>
        ) : null}
        {expense?.group_name && !inGroup ? (
          <span className="expense-kanban-tag" title={expense.group_name}>
            <i className="bi bi-collection" aria-hidden="true" /> {expense.group_name}
          </span>
        ) : null}
        {inQbo && !editableQbo ? (
          <span className="expense-kanban-tag expense-kanban-tag--qbo">
            <i className="bi bi-check2-circle" aria-hidden="true" /> In QBO
          </span>
        ) : null}
        {invoiceCap != null ? (
          <span
            className="expense-kanban-tag expense-kanban-tag--cap"
            title={`Invoice capped at ${formatCurrency(invoiceCap)} of ${formatCurrency(expense?.amount)}`}
          >
            <i className="bi bi-scissors" aria-hidden="true" /> Capped {formatCurrency(invoiceCap)}
          </span>
        ) : null}
      </div>

      {/* Deliberately a div, not a <footer>: App.css pins every footer element
          to the bottom of the viewport with position: fixed. */}
      <div className="expense-kanban-card__foot">
        <span title="Purchase date">
          <i className="bi bi-calendar3" aria-hidden="true" /> {formatDate(expense?.purchase_date) || '—'}
        </span>
        {expense?.paid_on ? (
          <span className="expense-kanban-card__paid" title="Reimbursed">
            <i className="bi bi-cash-coin" aria-hidden="true" /> {formatDate(expense.paid_on)}
          </span>
        ) : null}
        {expense?.invoiced_on ? (
          <span className="expense-kanban-card__invoiced" title="Invoiced to customer">
            <i className="bi bi-receipt" aria-hidden="true" /> {formatDate(expense.invoiced_on)}
          </span>
        ) : null}
        {attachments > 0 ? (
          <span title={`${attachments} attachment${attachments === 1 ? '' : 's'}`}>
            <i className="bi bi-paperclip" aria-hidden="true" /> {attachments}
          </span>
        ) : null}

        {editableQbo ? (
          // The whole card is a drag handle, so the checkbox has to keep its
          // pointer events to itself: stopping pointerdown prevents dnd-kit from
          // claiming the gesture, and stopping click prevents the card's own
          // handler from opening the review modal underneath.
          <label
            className={`expense-kanban-card__qbo${inQbo ? ' expense-kanban-card__qbo--on' : ''}`}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            title="Receipt uploaded to QuickBooks Online"
          >
            <input
              type="checkbox"
              checked={inQbo}
              onChange={(event) => onToggleQboReceipt(expense, event.target.checked)}
            />
            Receipt in QBO
          </label>
        ) : null}
      </div>
    </article>
  );
}
